"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";

export default function LearnDropdown({ programs, workshops }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("programs"); // 'programs' or 'workshops'
  const [showMorePrograms, setShowMorePrograms] = useState(false);
  const [showMoreWorkshops, setShowMoreWorkshops] = useState(false);
  const ref = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset show more state when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setShowMorePrograms(false);
      setShowMoreWorkshops(false);
    }
  }, [isOpen]);

  // Don't render if no data
  if (!programs?.length && !workshops?.length) return null;

  // Determine which programs to show on mobile
  const visiblePrograms = showMorePrograms 
    ? programs 
    : programs.slice(0, 3);

  // Determine which workshops to show on mobile
  const visibleWorkshops = showMoreWorkshops 
    ? workshops 
    : workshops.slice(0, 3);

  return (
    <>
      {/* Background overlay - only visible when dropdown is open */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/25 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      <div className="relative " ref={ref}>
        <button
          onClick={() => setIsOpen((o) => !o)}
          className="
            flex items-center
            px-3 py-2 text-base font-medium
            border border-[#002726] rounded-md
            text-[#002726]
            hover:text-white hover:bg-[#012726]
            transition-colors
            whitespace-nowrap
          "
        >
          Learn
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            className={`${
              isOpen ? "rotate-180" : ""
            } ml-1 transition-transform duration-200`}
          >
            <path
              d="M6 9L12 15L18 9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="sm:absolute fixed sm:left-0 left-0 sm:mt-2 top-[60px] sm:top-auto sm:w-[850px] w-screen max-w-[100vw] bg-white rounded-xl shadow-xl z-50">
            {/* Desktop View - Side by Side Layout */}
            <div className="hidden sm:flex w-full">
              {/* Programs Column */}
              <div className="p-4 w-1/2 border-r border-gray-400 ">
                <h2 className="text-[#002726] text-lg font-medium">
                  Mentorship Programs
                </h2>
                <p className="text-[#002726] text-sm mb-3">
                  Leap into a new career with our focused Programs
                </p>

                <div className="space-y-3">
                  {programs.map((prog) => (
                    <Link
                      key={prog.id}
                      href={`/${prog.slug}`}
                      onClick={() => setIsOpen(false)}
                      className="block"
                    >
                      <div className="flex items-start p-2  rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="w-26 h-16 relative rounded-md overflow-hidden flex-shrink-0">
                          <Image
                            src={prog.mainImage}
                            alt={prog.heading}
                            fill
                            sizes="64px"
                            className="object-cover grayscale"
                            style={{ filter: "grayscale(100%)" }}
                          />
                        </div>

                        <div className="ml-3 flex-grow">
                          <h3 className="text-[#002726] text-sm font-medium">
                            {prog.heading}
                          </h3>
                          <p className="text-[#002726] text-xs mb-1">
                            {prog.navmentor}
                          </p>

                          <div className="flex flex-wrap gap-1.5 items-center text-xs text-[#002726]">
                            <div className="flex items-center">
                              <Image
                                src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/duration.svg"
                                alt="Duration"
                                width={10}
                                height={10}
                                className="mr-1"
                              />
                              {prog.duration}
                            </div>
                            <div className="flex items-center">
                              <Image
                                src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/live.svg"
                                alt="Type"
                                width={10}
                                height={10}
                                className="mr-1"
                              />
                              {prog.type}
                            </div>
                            {prog.date && (
                              <div className="flex items-center">
                                <Image
                                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/date.svg"
                                  alt="Date"
                                  width={10}
                                  height={10}
                                  className="mr-1"
                                />
                                {prog.date}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Workshops Column */}
              <div className="p-4 w-1/2">
                <h2 className="text-[#002726] text-lg font-medium">Workshops</h2>
                <p className="text-[#002726] text-sm mb-3">
                  Boost your skills with focused, hands-on Workshops
                </p>

                <div className="space-y-3">
                  {workshops &&
                    workshops.map((workshop) => (
                      <Link
                        key={workshop.id}
                        href={`/workshops/${workshop.slug}`}
                        onClick={() => setIsOpen(false)}
                        className="block"
                      >
                        <div className="flex items-start p-2  rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="w-24 h-16 relative rounded-md overflow-hidden flex-shrink-0">
                            <Image
                              src={workshop.mainImage}
                              alt={workshop.heading}
                              fill
                              sizes="64px"
                              className="object-cover grayscale"
                              style={{ filter: "grayscale(100%)" }}
                            />
                          </div>

                          <div className="ml-3 flex-grow">
                            <h3 className="text-[#002726] text-sm font-medium">
                              {workshop.heading}
                            </h3>
                            <p className="text-[#002726] text-xs mb-1">
                              {workshop.mentornav}
                            </p>

                            <div className="flex flex-wrap gap-1.5 items-center text-xs text-[#002726]">
                              <div className="flex items-center">
                                <Image
                                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/duration.svg"
                                  alt="Duration"
                                  width={10}
                                  height={10}
                                  className="mr-1"
                                />
                                {workshop.duration}
                              </div>
                              <div className="flex items-center">
                                <Image
                                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/live.svg"
                                  alt="Type"
                                  width={10}
                                  height={10}
                                  className="mr-1"
                                />
                                {workshop.time}
                              </div>
                              {workshop.date && (
                                <div className="flex items-center">
                                  <Image
                                    src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/date.svg"
                                    alt="Date"
                                    width={10}
                                    height={10}
                                    className="mr-1"
                                  />
                                  {workshop.date}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            </div>

            {/* Mobile View with Tabs */}
            <div className="sm:hidden flex flex-col w-full">
              {/* Tabs */}
              <div className="flex border-b border-opacity-30">
                <button
                  onClick={() => setActiveTab("programs")}
                  className={`py-3 px-5 text-base font-medium w-1/2 text-center ${
                    activeTab === "programs"
                      ? "text-[#002726] border-b-2 border-[#002726] bg-gray-50"
                      : "text-gray-500"
                  }`}
                >
                  Mentorship Programs
                </button>
                <button
                  onClick={() => setActiveTab("workshops")}
                  className={`py-3 px-5 text-base font-medium w-1/2 text-center ${
                    activeTab === "workshops"
                      ? "text-[#002726] border-b-2 border-[#002726] bg-gray-50"
                      : "text-gray-500"
                  }`}
                >
                  Workshops
                </button>
              </div>

              {/* Content - Only showing active tab content */}
              <div className="overflow-auto max-h-[80vh]">
                {/* Programs Section */}
                {activeTab === "programs" && (
                  <div className="p-4 w-full">
                    <h2 className="text-[#002726] text-base font-medium">
                      Mentorship Programs
                    </h2>
                    <p className="text-[#002726] text-xs mb-3">
                      Leap into a new career with our focused Programs
                    </p>

                    <div className="space-y-3">
                      {visiblePrograms.map((prog) => (
                        <Link
                          key={prog.id}
                          href={`/${prog.slug}`}
                          onClick={() => setIsOpen(false)}
                          className="block"
                        >
                          <div className="flex items-start p-2  rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="w-28 h-16 relative rounded-md overflow-hidden flex-shrink-0">
                              <Image
                                src={prog.mainImage}
                                alt={prog.heading}
                                fill
                                sizes="48px"
                                className="object-cover grayscale"
                                style={{ filter: "grayscale(100%)" }}
                              />
                            </div>

                            <div className="ml-2 flex-grow">
                              <h3 className="text-[#002726] text-sm font-medium">
                                {prog.heading}
                              </h3>
                              <p className="text-[#002726] text-xs mb-1">
                                {prog.navmentor}
                              </p>

                              <div className="flex flex-wrap gap-1.5 items-center text-xs text-[#002726]">
                                <div className="flex items-center">
                                  <Image
                                    src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/duration.svg"
                                    alt="Duration"
                                    width={10}
                                    height={10}
                                    className="mr-1"
                                  />
                                  {prog.duration}
                                </div>
                                <div className="flex items-center">
                                  <Image
                                    src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/live.svg"
                                    alt="Type"
                                    width={10}
                                    height={10}
                                    className="mr-1"
                                  />
                                  {prog.type}
                                </div>
                                {prog.date && (
                                  <div className="flex items-center">
                                    <Image
                                      src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/date.svg"
                                      alt="Date"
                                      width={10}
                                      height={10}
                                      className="mr-1"
                                    />
                                    {prog.date}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                      
                      {/* Show More button for Programs */}
                      {programs.length > 3 && !showMorePrograms && (
                        <button
                          onClick={() => setShowMorePrograms(true)}
                          className="w-full py-2 text-center text-[#002726] font-medium border border-[#002726] rounded-lg mt-3"
                        >
                          Show More
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Workshops Section */}
                {activeTab === "workshops" && (
                  <div className="p-4 w-full">
                    <h2 className="text-[#002726] text-base font-medium">
                      Workshops
                    </h2>
                    <p className="text-[#002726] text-xs mb-3">
                      Boost your skills with focused, hands-on Workshops
                    </p>

                    <div className="space-y-3">
                      {visibleWorkshops.map((workshop) => (
                        <Link
                          key={workshop.id}
                          href={`/workshops/${workshop.slug}`}
                          onClick={() => setIsOpen(false)}
                          className="block"
                        >
                          <div className="flex items-start p-2 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="w-28 h-16 relative rounded-md overflow-hidden flex-shrink-0">
                              <Image
                                src={workshop.mainImage}
                                alt={workshop.heading}
                                fill
                                sizes="48px"
                                className="object-cover grayscale"
                                style={{ filter: "grayscale(100%)" }}
                              />
                            </div>

                            <div className="ml-2 flex-grow">
                              <h3 className="text-[#002726] text-sm font-medium">
                                {workshop.heading}
                              </h3>
                              <p className="text-[#002726] text-xs mb-1">
                                {workshop.mentornav}
                              </p>

                              <div className="flex flex-wrap gap-1.5 items-center text-xs text-[#002726]">
                                <div className="flex items-center">
                                  <Image
                                    src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/duration.svg"
                                    alt="Duration"
                                    width={10}
                                    height={10}
                                    className="mr-1"
                                  />
                                  {workshop.duration}
                                </div>
                                <div className="flex items-center">
                                  <Image
                                    src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/live.svg"
                                    alt="Type"
                                    width={10}
                                    height={10}
                                    className="mr-1"
                                  />
                                  {workshop.time}
                                </div>
                                {workshop.date && (
                                  <div className="flex items-center">
                                    <Image
                                      src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/date.svg"
                                      alt="Date"
                                      width={10}
                                      height={10}
                                      className="mr-1"
                                    />
                                    {workshop.date}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                      
                      {/* Show More button for Workshops */}
                      {workshops.length > 3 && !showMoreWorkshops && (
                        <button
                          onClick={() => setShowMoreWorkshops(true)}
                          className="w-full py-2 text-center text-[#002726] font-medium border border-[#002726] rounded-lg mt-3"
                        >
                          Show More
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}